import { useMemo, useState } from 'react';

import { faImage } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faChevronDown,
  faChevronUp,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Skeleton } from '@repo/videa-components/src/ui/skeleton';
import { useAtomValue } from 'jotai';

import { imageQualityAtom } from '@/modules/annotator/atoms';
import { getImageQualityFindingsFromImageQuality } from '@/utils/claimSummary/imageFindings';

import { FindingItem } from './FindingItem';

interface DocumentationProps {
  activeImageId?: number;
  imageTypeMap?: Map<number, string>;
  isLoading?: boolean;
  scheduledTeeth?: Set<string> | null;
  sortedAndFilteredImages?: {
    fmxIndex?: number | null;
    imageManifestId?: number | null;
  }[];
}

export const Documentation = ({
  activeImageId,
  imageTypeMap,
  isLoading,
  scheduledTeeth,
  sortedAndFilteredImages,
}: DocumentationProps) => {
  const imageQuality = useAtomValue(imageQualityAtom);
  const [isExpanded, setIsExpanded] = useState(true);

  const slotsWithImages = useMemo(() => {
    if (!sortedAndFilteredImages?.length) return new Set<number>();
    const set = new Set<number>();
    for (const img of sortedAndFilteredImages) {
      const slot = img.fmxIndex;
      const hasRealImage = img.imageManifestId != null;
      if (hasRealImage && slot != null && slot >= 0 && slot <= 17) {
        set.add(slot);
      }
    }
    return set;
  }, [sortedAndFilteredImages]);

  // Only show findings for images that are actually displayed in the FMX grid
  // (not for images that only appear in the bottom bar). Include activeImageId
  // when an image is selected so single-image view still shows that image's findings.
  const displayedImageIds = useMemo(() => {
    const set = new Set<number>();
    if (activeImageId != null) set.add(activeImageId);
    if (sortedAndFilteredImages?.length) {
      for (const img of sortedAndFilteredImages) {
        const id = img.imageManifestId;
        if (id != null) set.add(id);
      }
    }
    return set;
  }, [sortedAndFilteredImages, activeImageId]);

  const imageQualityForView = useMemo(() => {
    if (!imageQuality?.length || displayedImageIds.size === 0) return [];
    return imageQuality.filter((img) => displayedImageIds.has(img.image_id));
  }, [imageQuality, displayedImageIds]);

  const findings = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return getImageQualityFindingsFromImageQuality(
      imageQualityForView,
      imageTypeMap,
      activeImageId,
      scheduledTeeth,
      slotsWithImages,
    );
  }, [
    imageQualityForView,
    imageTypeMap,
    isLoading,
    activeImageId,
    scheduledTeeth,
    slotsWithImages,
  ]);

  // Show loading state or hide if no findings
  if (!isLoading && findings.length === 0) {
    return null;
  }

  return (
    <div className="vai-mt-4 [@media(max-height:570px)]:vai-mt-2 vai-border vai-border-zinc-800 vai-rounded-[6px] vai-overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="vai-w-full vai-p-3 [@media(max-height:570px)]:vai-p-2 vai-bg-zinc-950 vai-flex vai-items-center vai-justify-between hover:vai-bg-zinc-900 vai-transition-colors"
      >
        <div className="vai-flex vai-items-center vai-gap-2">
          <span className="vai-text-zinc-200 vai-text-sm vai-font-medium">
            <Trans>Documentation</Trans>
          </span>
          {findings.length > 0 && (
            <div className="vai-flex vai-items-center vai-gap-1 vai-bg-amber-500/20 vai-text-amber-400 vai-text-xs vai-px-2 vai-py-0.5 vai-rounded-full">
              <span>{findings.length}</span>
              <span>
                <Trans>findings</Trans>
              </span>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="vai-w-3 vai-h-3"
              />
            </div>
          )}
        </div>
        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
          className="vai-text-zinc-400 vai-w-3 vai-h-3"
        />
      </button>

      {isExpanded && (
        <div className="vai-p-3 [@media(max-height:570px)]:vai-p-2">
          <p className="vai-text-xs vai-text-zinc-500 vai-mb-3 vai-italic">
            <Trans>
              Image Quality is intended for insurance claim use only and is not
              a cleared medical device feature.
            </Trans>
          </p>
          <div className="vai-flex vai-items-center vai-gap-2 vai-mb-2">
            <FontAwesomeIcon
              icon={faImage}
              className="vai-text-zinc-400 vai-w-3.5 vai-h-3.5"
            />
            <span className="vai-text-zinc-300 vai-text-xs vai-uppercase vai-tracking-wider">
              <Trans>Imaging</Trans>
            </span>
          </div>

          {isLoading ? (
            <div className="vai-flex vai-flex-col vai-gap-2">
              <Skeleton className="vai-w-full vai-h-14 vai-rounded-lg" />
            </div>
          ) : (
            <div className="vai-flex vai-flex-col">
              {findings.map((finding) => (
                <FindingItem key={finding.id} finding={finding} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
